import React, { useCallback, useEffect, useState } from 'react';
import { SxStyle } from '../../types';
import { useStringsContext } from '../../contexts/strings';
import BaseDialog, { BaseDialogProps } from './base-dialog';
import Image from './image';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import ImageDropzone, { FileWithDimensions, ImageDropzoneProps } from './image-dropzone';

const styles: SxStyle = {
  preview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 4,
  },
  image: {
    width: 'auto',
    height: '120px',
    objectFit: 'contain',
    borderRadius: '8px',
  },

  baseStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
  },
  focusedStyle: {
    borderColor: '#2196f3',
  },
  acceptStyle: {
    borderColor: '#00e676',
  },
  rejectStyle: {
    borderColor: '#ff1744',
  },
};

export type ImageFile = {
  preview: string;
} & FileWithDimensions;

interface Props
  extends Pick<BaseDialogProps, 'open' | 'onClose'>,
    Pick<ImageDropzoneProps, 'maxFiles' | 'maxSize'> {
  onSave: (file: ImageFile[]) => void;
}

const ImageDropzoneDialog = ({ open, onClose, onSave, maxFiles }: Props) => {
  const { strings } = useStringsContext();
  const [acceptedFiles, setAcceptedFiles] = useState<ImageFile[] | null>(null);

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => acceptedFiles?.forEach((file) => URL.revokeObjectURL(file.preview));
  }, [acceptedFiles]);

  const handleAcceptedFiles = (files: FileWithDimensions[]) => {
    const filesWithPreview = files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    setAcceptedFiles(filesWithPreview);
  };

  const handleSubmit = useCallback(() => {
    if (!acceptedFiles) {
      return;
    }
    onSave(acceptedFiles);
    setAcceptedFiles(null);
  }, [onSave, acceptedFiles]);

  return (
    <BaseDialog open={open} onClose={onClose} title={strings.actions.uploadImage}>
      <DialogContent dividers>
        <ImageDropzone onAcceptedFiles={handleAcceptedFiles} maxFiles={maxFiles} />
        {acceptedFiles && (
          <Stack direction="row" spacing={2} sx={styles.preview}>
            {acceptedFiles.map((file) => (
              <Stack direction="column" spacing={2} alignItems="center" key={file.name}>
                <Image
                  src={file.preview}
                  sx={styles.image}
                  // Revoke data uri after image is loaded
                  onLoad={() => {
                    URL.revokeObjectURL(file.preview);
                  }}
                />
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    const filteredFiles = acceptedFiles.filter((f) => f !== file);
                    setAcceptedFiles(filteredFiles.length > 0 ? filteredFiles : null);
                  }}
                >
                  {strings.actions.removeImage}
                </Button>
              </Stack>
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Stack direction="row" gap={2}>
          <Button color="primary" onClick={onClose}>
            {strings.actions.cancel}
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            loading={false}
            disabled={acceptedFiles === null}
          >
            {strings.actions.upload}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </BaseDialog>
  );
};

export default ImageDropzoneDialog;
